import { useState } from 'react';
import { format } from 'date-fns';
import { RefreshCw } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { ChecksBadge } from '@/components/shared/automation-reply-badges';
import { useToolDefinitions } from '@/hooks/use-tool-definitions';
import { rerunAutomationChecks } from '@/hooks/use-automation-replies';
import { humanize } from '@/lib/automation-replies';
import { getApiErrorMessage } from '@/lib/utils';
import { PillButton } from '@/modules/simulator/ui/pill-button';
import { ToolRefChip } from '@/modules/back-testing/datasets/tools/tool-ref-chip';
import { SourceChip } from '@/modules/back-testing/datasets/sources/source-chip';
import { SOURCE_KIND_LABEL, sourceOptionsOf } from '@/modules/back-testing/datasets/sources/source-kinds';
import { useSourceOptions } from '@/modules/back-testing/datasets/sources/use-source-options';
import type { CheckSources, ToolRef } from '@/services/accountIntentConfig.service';
import type { AutomationReply } from '@/services/automation-reply.service';

/**
 * One reply's automation checks in its details: how it ended, what it missed or did that it must not,
 * and everything it called and drew on, with a way to check it again against the checks as they are now.
 */

function Finding({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className='flex flex-col gap-1.5'>
			<p className='text-[12px] text-gray-800'>{label}</p>
			<div className='flex flex-wrap gap-1.5'>{children}</div>
		</div>
	);
}

export function ReplyChecks({ reply, canCheck }: { reply: AutomationReply; canCheck: boolean }) {
	const { data: definitions = [] } = useToolDefinitions();
	const names = new Map(useSourceOptions().map((option) => [option.id, option.name]));
	const [running, setRunning] = useState(false);
	const { checks } = reply;

	async function checkAgain() {
		setRunning(true);
		try {
			const entry = await rerunAutomationChecks(reply.id);
			toast.success(entry.checks ? 'Checked again' : "None of this reply's intents has checks now");
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Failed to check the reply'));
		} finally {
			setRunning(false);
		}
	}

	// A tool called twice with other arguments shows once: the checks never read arguments.
	const tools = (list: ToolRef[], tone?: 'bad') =>
		list
			.filter((tool, index) => list.findIndex((other) => other.name === tool.name && other.provider === tool.provider) === index)
			.map((tool) => <ToolRefChip key={`${tool.provider ?? ''}:${tool.name}`} tool={tool} definitions={definitions} tone={tone} />);
	const sources = (list: CheckSources) =>
		sourceOptionsOf(list, names).map((option) => (
			<SourceChip key={`${option.kind}:${option.id}`} kind={SOURCE_KIND_LABEL[option.kind]} value={option.name} />
		));
	const missingSources = checks ? sourceOptionsOf(checks.sources.missing, names) : [];

	return (
		<section className='flex flex-col gap-2' aria-label='Automation checks'>
			<div className='flex items-center justify-between gap-2'>
				<p className='text-[12px] font-medium text-ink'>Automation checks</p>
				{canCheck && (
					<PillButton variant='ghost' className='gap-1' disabled={running} onClick={() => void checkAgain()}>
						<RefreshCw className={running ? 'size-3 animate-spin' : 'size-3'} />
						{checks ? 'Check again' : 'Check'}
					</PillButton>
				)}
			</div>
			{!checks ? (
				<p className='text-[12px] text-gray-900'>
					Not checked. Replies are checked shortly after they go out when one of their intents has checks, set under
					Automation &gt; Intents.
				</p>
			) : (
				<div className='flex flex-col gap-3 rounded-[14px] border border-gray-300 px-4 py-3'>
					<div className='flex items-center gap-2'>
						<ChecksBadge checks={checks} />
						<span className='text-[12px] text-gray-900 capitalize'>
							{checks.intents.map(humanize).join(', ')} · {format(new Date(checks.checkedAt), 'd MMM yyyy, HH:mm')}
						</span>
					</div>
					{checks.note && <p className='text-[13px] leading-[18px] text-ink'>{checks.note}</p>}
					{checks.tools.missing.length > 0 && <Finding label='Required tools it never called'>{tools(checks.tools.missing, 'bad')}</Finding>}
					{checks.tools.forbidden.length > 0 && <Finding label='Forbidden tools it called'>{tools(checks.tools.forbidden, 'bad')}</Finding>}
					{missingSources.length > 0 && <Finding label='Required sources it did not use'>{sources(checks.sources.missing)}</Finding>}
					{checks.tools.called && (
						<Finding label='Tools it called'>
							{checks.tools.called.length ? tools(checks.tools.called) : <span className='text-[12px] text-gray-900'>None</span>}
						</Finding>
					)}
					{checks.sources.used && (
						<Finding label='Sources it used'>
							{sourceOptionsOf(checks.sources.used, names).length ? (
								sources(checks.sources.used)
							) : (
								<span className='text-[12px] text-gray-900'>None</span>
							)}
						</Finding>
					)}
				</div>
			)}
		</section>
	);
}
